import { getDateTimeFormat } from "../get-date-time-format";

const TIME_WITH_SECONDS = /^([0-1]?[0-9]|2[0-3]):([0-5][0-9]):([0-5][0-9])$/;
const TIME_WITHOUT_SECONDS = /^([0-1]?[0-9]|2[0-3]):([0-5][0-9])$/;

type FormatTimeOfDayProps = {
	time: string | undefined;
	locale?: string | undefined;
	alwaysShowMinutes?: boolean;
};

/**
 * Formats a wall-clock "HH:mm" / "HH:mm:ss" string using the locale's own hour cycle:
 * "8:30 AM" in en-US, "8 h 30" in fr-CA, "8:30" in fr.
 *
 * On the hour the minutes are dropped ("12 AM", "9 h"), which is both what the formatter this
 * replaced did and what the OQLF prescribes for fr-CA. Pass `alwaysShowMinutes` to keep them
 * ("12:00 AM"): the permit schedule table renders one time per row and needs them to line up. That
 * split is not new — the schedule table already carried its own copy of this formatter that always
 * printed minutes, so collapsing both onto one default would have restyled one screen or the other.
 */
export const formatTimeOfDay = ({
	time,
	locale = "en-US",
	alwaysShowMinutes = false,
}: FormatTimeOfDayProps): string => {
	if (!time) {
		return "-";
	}

	const match = TIME_WITH_SECONDS.exec(time) ?? TIME_WITHOUT_SECONDS.exec(time);
	if (!match) {
		return "Invalid Time";
	}

	const hour = Number(match[1]);
	const minute = Number(match[2]);

	return getDateTimeFormat(locale, {
		hour: "numeric",
		minute: minute === 0 && !alwaysShowMinutes ? undefined : "2-digit",
		timeZone: "UTC",
	}).format(new Date(Date.UTC(2000, 0, 1, hour, minute)));
};
